import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Avatar,
  Typography,
  Chip,
  Stack,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  Badge
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode2 as QrCodeIcon,
  Download as DownloadIcon,
  Assessment as AssessmentIcon,
  PersonAdd as PersonAddIcon,
  Add as AddIcon,
  Search as SearchIcon,
  CloudDownload as CloudDownloadIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  fetchQRCodes,
  deleteQRCode,
  downloadQRImage,
  generateQRImageUrl
} from '../qrCodeApi';
import { fetchUsers } from '../usersApi';

const QRCodes = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [qrCodes, setQRCodes] = useState([]);
  const [filteredQRCodes, setFilteredQRCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // QR Preview Modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedQR, setSelectedQR] = useState(null);

  useEffect(() => {
    loadData();
    
    // 🆕 Auto-refresh every 30 seconds to show new scans/downloads
    const interval = setInterval(() => {
      loadData(true); // Silent refresh
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, filterUser, filterStatus, qrCodes]);

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    
    try {
      const [qrData, userData] = await Promise.all([
        fetchQRCodes(),
        fetchUsers()
      ]);
      
      console.log('📊 QR Codes loaded:', qrData.qrCodes?.length || 0);
      console.log('Total Scans:', qrData.qrCodes?.reduce((sum, qr) => sum + (qr.ScanCount || 0), 0));
      console.log('Total Downloads:', qrData.qrCodes?.reduce((sum, qr) => sum + (qr.DownloadCount || 0), 0));
      
      setQRCodes(qrData.qrCodes || []);
      setUsers(userData.filter(u => u.Role !== 'Driver'));
    } catch (error) {
      console.error('Error loading data:', error);
      if (!silent) {
        Swal.fire(t('error'), t('loadDataError') || 'Failed to load data', 'error');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...qrCodes];

    if (searchTerm) {
      filtered = filtered.filter(qr =>
        qr.Code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterUser) {
      if (filterUser === 'unassigned') {
        filtered = filtered.filter(qr => !qr.UserId);
      } else {
        filtered = filtered.filter(qr => qr.UserId === parseInt(filterUser));
      }
    }

    if (filterStatus !== 'all') {
      const isActive = filterStatus === 'active';
      filtered = filtered.filter(qr => qr.IsActive === isActive);
    }

    setFilteredQRCodes(filtered);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t('areYouSure'),
      text: t('deleteQRWarning') || 'This will delete the QR code and all its scan history',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('yesDeleteIt'),
      cancelButtonText: t('cancel')
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteQRCode(id);
          Swal.fire(t('deleted'), t('qrCodeDeleted') || 'QR code deleted', 'success');
          loadData();
        } catch (error) {
          Swal.fire(t('error'), t('deleteError') || 'Delete failed', 'error');
        }
      }
    });
  };

  const handleDownload = async (qrCode) => {
    try {
      await downloadQRImage(qrCode);
      Swal.fire({
        icon: 'success',
        title: t('success'),
        text: t('qrDownloaded') || 'QR code downloaded successfully',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire(t('error'), t('downloadError') || 'Download failed', 'error');
    }
  };

  const handlePreview = (qrCode) => {
    setSelectedQR(qrCode);
    setPreviewOpen(true);
  };

  const getUserName = (userId) => {
    if (!userId) return '-';
    const user = users.find(u => u.Id === userId);
    return user ? user.FullName : `User #${userId}`;
  };

  // 🆕 Calculate total downloads
  const totalDownloads = qrCodes.reduce((sum, qr) => sum + (qr.DownloadCount || 0), 0);
  const totalScans = qrCodes.reduce((sum, qr) => sum + (qr.ScanCount || 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={1}>
              <QrCodeIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h5" fontWeight="bold">
                {t('manageQRCodes') || 'Manage QR Codes'}
              </Typography>
            </Stack>
          }
          subheader={t('qrCodesSubtitle') || 'Track and manage your app download QR codes'}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={() => loadData()}
                sx={{ borderRadius: 50 }}
              >
                {t('refresh')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/admin/qrcodes/generate')}
                sx={{ borderRadius: 50 }}
              >
                {t('generateQRCodes') || 'Generate QR Codes'}
              </Button>
            </Stack>
          }
          sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
        />
        
        <CardContent>
          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder={t('searchByCode') || 'Search by code...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{t('filterByUser') || 'Filter by User'}</InputLabel>
                <Select
                  value={filterUser}
                  onChange={(e) => setFilterUser(e.target.value)}
                  label={t('filterByUser') || 'Filter by User'}
                >
                  <MenuItem value="">{t('allUsers') || 'All Users'}</MenuItem>
                  <MenuItem value="unassigned">{t('unassigned') || 'Unassigned'}</MenuItem>
                  {users.map(user => (
                    <MenuItem key={user.Id} value={user.Id}>
                      {user.FullName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{t('status')}</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label={t('status')}
                >
                  <MenuItem value="all">{t('allStatus') || 'All Status'}</MenuItem>
                  <MenuItem value="active">{t('active')}</MenuItem>
                  <MenuItem value="inactive">{t('inactive')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Statistics Summary */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText', textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold">
                  {qrCodes.length}
                </Typography>
                <Typography variant="body2">{t('totalQRCodes') || 'Total QR Codes'}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText', textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold">
                  {qrCodes.filter(qr => qr.IsActive).length}
                </Typography>
                <Typography variant="body2">{t('activeQRCodes') || 'Active'}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, bgcolor: 'info.light', color: 'info.contrastText', textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold">
                  {totalScans}
                </Typography>
                <Typography variant="body2">{t('totalScans') || 'Total Scans'}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, bgcolor: 'warning.light', color: 'warning.contrastText', textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold">
                  {totalDownloads}
                </Typography>
                <Typography variant="body2">{t('totalDownloads') || 'Total Downloads'}</Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Table */}
          <TableContainer component={Paper} elevation={0}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead sx={{ bgcolor: 'grey.100' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('qrCode') || 'QR Code'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('code')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('assignedTo') || 'Assigned To'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>{t('scans')}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>{t('downloads')}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>{t('status')}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>{t('actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredQRCodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography color="textSecondary">
                        {t('noQRCodesFound') || 'No QR codes found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredQRCodes.map((qr, index) => (
                    <TableRow key={qr.Id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Avatar
                          src={generateQRImageUrl(qr.TrackingUrl)}
                          variant="rounded"
                          sx={{ width: 50, height: 50, cursor: 'pointer' }}
                          onClick={() => handlePreview(qr)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                          {qr.Code}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          #{qr.Id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {qr.UserId ? (
                          <Chip
                            label={getUserName(qr.UserId)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Chip label={t('unassigned')} size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Badge badgeContent={qr.ScanCount} color="info" max={999}>
                          <Chip label={qr.ScanCount || 0} size="small" color="info" />
                        </Badge>
                      </TableCell>
                      <TableCell align="center">
                        <Badge badgeContent={qr.DownloadCount} color="success" max={999}>
                          <Chip 
                            label={qr.DownloadCount || 0} 
                            size="small" 
                            color="success"
                            icon={<CloudDownloadIcon />}
                          />
                        </Badge>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={qr.IsActive ? t('active') : t('inactive')}
                          size="small"
                          color={qr.IsActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                          <Tooltip title={t('viewStats') || 'View Statistics'}>
                            <IconButton
                              color="info"
                              size="small"
                              onClick={() => navigate(`/admin/qrcodes/stats/${qr.Id}`)}
                            >
                              <AssessmentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('download')}>
                            <IconButton
                              color="success"
                              size="small"
                              onClick={() => handleDownload(qr)}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('assign') || 'Assign to User'}>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => navigate(`/admin/qrcodes/assign/${qr.Id}`)}
                            >
                              <PersonAddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('edit')}>
                            <IconButton
                              color="warning"
                              size="small"
                              onClick={() => navigate(`/admin/qrcodes/edit/${qr.Id}`)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('delete')}>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDelete(qr.Id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 🆕 Auto-refresh indicator */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="textSecondary">
              {t('autoRefresh') || 'Auto-refreshing every 30 seconds'} • {t('lastUpdate') || 'Last update'}: {new Date().toLocaleTimeString()}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* QR Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>
          {t('qrCodePreview') || 'QR Code Preview'}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          {selectedQR && (
            <Box>
              <img
                src={generateQRImageUrl(selectedQR.TrackingUrl)}
                alt={`QR-${selectedQR.Code}`}
                style={{ width: '100%', maxWidth: 300, height: 'auto', borderRadius: 8 }}
              />
              <Typography variant="h6" sx={{ mt: 2, fontFamily: 'monospace', fontWeight: 'bold' }}>
                {selectedQR.Code}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1, wordBreak: 'break-all', px: 2 }}>
                {selectedQR.TrackingUrl}
              </Typography>
              
              {/* 🆕 Stats in preview */}
              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
                <Chip label={`${t('scans')}: ${selectedQR.ScanCount || 0}`} color="info" />
                <Chip label={`${t('downloads')}: ${selectedQR.DownloadCount || 0}`} color="success" />
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'grey.50', p: 2 }}>
          <Button onClick={() => setPreviewOpen(false)} variant="outlined" sx={{ borderRadius: 50 }}>
            {t('close')}
          </Button>
          {selectedQR && (
            <Button
              onClick={() => handleDownload(selectedQR)}
              variant="contained"
              startIcon={<DownloadIcon />}
              sx={{ borderRadius: 50 }}
            >
              {t('download')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QRCodes;
